import { StickyNote } from 'lucide-react';
import { memo, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { btLayoutSidebarActions } from '@common-reducers/BtLayoutSidebarReducer';
import { DBWaNoteThunks, waNoteSelectByQuery } from '@common-reducers/DBServiceThunks';
import { namespace } from "@common-config/constants";

type Props = {
    waChatId?: string; // Optional: if provided, uses this; otherwise falls back to current chat
    size?: number;
}

export default memo(function NotesButton({ waChatId: propWaChatId, size }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const { isOpen, componentToShow } = useAppSelector((state) => state.BtLayoutSidebarReducer);

    // Notes logic - use prop if provided, otherwise fall back to current chat
    const currentChat = useAppSelector((state) => state.WhatsAppReducer.currentChat);
    const chatId = propWaChatId || currentChat?.id?._serialized;

    const notes = useAppSelector((state) => {
        if (!chatId) return [];
        return waNoteSelectByQuery(state.DBWaNoteReducer, { chatId });
    });

    const activeNotesCount = notes.filter(n => !n.deleted).length;

    // console.log(`[NotesButton] chatId: ${chatId}, notes: ${notes.length}, active: ${activeNotesCount}`);

    useEffect(() => {
        if (chatId) {
            // console.log(`[NotesButton] Fetching notes for ${chatId}`);
            // useCache: true skips the forceRefetch so RTK Query can dedupe by
            // args — revisiting a chat we already loaded is served from cache
            // instead of re-hitting the server on every chat switch.
            dispatch(DBWaNoteThunks.find({
                chatId,
                $sort: { createdAt: -1 },
                useCache: true,
            }));
        }
    }, [chatId, dispatch]);

    // Check if Notes sidebar is currently open
    const isNotesOpen = isOpen && componentToShow === 'Notes';

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (isNotesOpen) {
            dispatch(btLayoutSidebarActions.closeSidebar());
        } else {
            dispatch(btLayoutSidebarActions.openWithComponent('Notes'));
        }
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        className={`p-1 rounded transition-colors cursor-pointer hover:text-blue-500 relative ${isNotesOpen ? 'text-blue-500' : 'text-gray-500'}`}
                        onClick={handleClick}
                    >
                        <StickyNote size={size || 20} className="currentColor" strokeWidth={2} />
                        {activeNotesCount > 0 && (
                            <div className="absolute top-[-4px] right-[-4px] bg-blue-500 text-white text-[10px] min-w-[16px] h-4 flex items-center justify-center rounded-full border-2 border-white dark:border-gray-800 px-0.5 pointer-events-none">
                                {activeNotesCount > 9 ? '9+' : activeNotesCount}
                            </div>
                        )}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                    <p>{t('labels.notes')}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
})
